import React from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * APEX v7.1 — Auto-Import Indicator
 *
 * Petit indicateur visuel + bouton refresh manuel pour l'auto-import.
 *
 * États visuels :
 *   - Pas connecté à Riot : caché
 *   - Idle (sync OK) : point cyan + texte "Sync · il y a Xm"
 *   - En cours : spinner animé
 *   - Erreur récente : point amber + tooltip
 *
 * Props:
 *   - isImporting    : bool — en cours de fetch
 *   - lastImportAt   : Date | null
 *   - lastResult     : { added, skipped, errors, reason }
 *   - onManualRefresh: callback bouton manuel
 *   - hasRiotId      : bool — affiche/cache le composant
 */

function formatTimeAgo(date) {
  if (!date) return 'jamais';
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'à l\'instant';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}j`;
}

export default function AutoImportIndicator({
  isImporting,
  lastImportAt,
  lastResult,
  onManualRefresh,
  hasRiotId
}) {
  if (!hasRiotId) return null;

  const hasError = lastResult?.errors > 0 && lastResult?.added === 0;
  const added = lastResult?.added || 0;

  return (
    <button
      onClick={onManualRefresh}
      disabled={isImporting}
      title={
        isImporting
          ? 'Import en cours...'
          : `Sync auto · clique pour forcer un refresh${lastImportAt ? ` (dernier: il y a ${formatTimeAgo(lastImportAt)})` : ''}`
      }
      className={`
        group flex items-center gap-2 px-2.5 py-1.5 border transition
        ${isImporting
          ? 'border-cyan-400/30 bg-cyan-400/5 cursor-wait'
          : hasError
            ? 'border-amber-400/30 hover:border-amber-400 hover:bg-amber-400/5'
            : 'border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900'
        }
      `}
    >
      {/* Status dot */}
      <div className={`
        w-1.5 h-1.5 rounded-full
        ${isImporting
          ? 'bg-cyan-400 animate-pulse'
          : hasError
            ? 'bg-amber-400'
            : added > 0
              ? 'bg-cyan-400'
              : 'bg-zinc-600'
        }
      `} />

      {/* Icon */}
      <RefreshCw
        size={11}
        className={`text-zinc-500 group-hover:text-zinc-300 ${isImporting ? 'animate-spin text-cyan-400' : ''}`}
      />

      {/* Texte */}
      <span className="text-[10px] tracking-wider uppercase font-mono text-zinc-500 group-hover:text-zinc-300 hidden sm:inline">
        {isImporting
          ? 'SYNC...'
          : lastImportAt
            ? `Sync ${formatTimeAgo(lastImportAt)}`
            : 'SYNC'
        }
      </span>
    </button>
  );
}
